import { LitElement, css, html, unsafeCSS } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import sheet from './css/bb-chat-contact.css?inline'
import type { ContactDetailsInputHint } from './bbcore/types'

// Renders an `input_needed:contact_details` hint as a phone + email form.
// Symmetric to bb-chat-fulfillment: backend gates the precondition (phone
// is required for every order, Trello #126), widget renders the form,
// host page can re-skin via shadow parts (`::part(form)`, `::part(intro)`,
// `::part(field-phone)`, `::part(input-phone)`, `::part(field-email)`,
// `::part(input-email)`, `::part(cta)`, etc.).
//
// On Continue:
//   1. dispatches `set-contact {phone, email}` (composed) — <bb-widget>
//      catches it, calls controller.setContact (POST /contact), then
//      sends the follow-up "Okay, I'm ready to check out." so the backend
//      computes the next hint (submit_order) on the same beat.
//   2. emits `widget-affordance {kind: "contact-continue"}` for telemetry.
//
// Validation rules (per Trello #126 decision):
//   - phone: required, ≥7 raw digits (loose enough for international
//     formats; tight enough to reject obvious mistakes). Backend
//     re-validates via libphonenumber.
//   - email: optional but if present must look like an email.
@customElement('bb-chat-contact')
export class BbChatContact extends LitElement {
  static styles = css`${unsafeCSS(sheet)}`

  @property({ attribute: false }) hint!: ContactDetailsInputHint
  @property({ type: Boolean }) disabled = false
  @property({ type: Boolean }) active = true
  @property({ reflect: true }) layout: 'inline' | 'stacked' = 'inline'

  // Initial values can be pre-stamped by the host via attributes — useful
  // when oishi's session already knows the logged-in customer's phone.
  // Empty strings = no prefill.
  @property({ type: String, attribute: 'initial-phone' }) initialPhone = ''
  @property({ type: String, attribute: 'initial-email' }) initialEmail = ''

  @state() private _phone = ''
  @state() private _email = ''
  @state() private _busy = false
  @state() private _showPhoneError = false

  willUpdate(changed: Map<string, unknown>) {
    if (changed.has('hint')) {
      // New hint id → reset the form so a previous turn's typed value
      // doesn't leak into the next ask. Re-apply the host's initials.
      this._phone = this.initialPhone
      this._email = this.initialEmail
      this._busy = false
      this._showPhoneError = false
    }
    if (changed.has('disabled')) this._busy = false
  }

  private get _phoneOk(): boolean {
    return this._phone.replace(/\D/g, '').length >= 7
  }

  private get _emailOk(): boolean {
    // Optional. Empty = OK. Otherwise must look like an email.
    const v = this._email.trim()
    return !v || /\S+@\S+\.\S+/.test(v)
  }

  private get _formOk(): boolean {
    return this._phoneOk && this._emailOk
  }

  render() {
    const off = this.disabled || !this.active
    const ctaOff = off || this._busy || !this._formOk
    return html`
      <form
        class="form"
        part="form"
        @submit=${this._onSubmit}
      >
        <p class="intro" part="intro">
          Your contact details so we can confirm your order.
        </p>
        <div class="field" part="field field-phone">
          <label for="bb-contact-phone" part="label label-phone">
            Phone number
          </label>
          <input
            id="bb-contact-phone"
            part="input input-phone"
            type="tel"
            inputmode="tel"
            autocomplete="tel"
            placeholder="+27 82 123 4567"
            .value=${this._phone}
            ?disabled=${off}
            required
            @input=${this._onPhoneInput}
            @blur=${() => { this._showPhoneError = !this._phoneOk }}
          />
          <p
            class="error"
            part="error error-phone"
            ?hidden=${!this._showPhoneError}
          >
            Enter a valid phone number (at least 7 digits).
          </p>
        </div>
        <div class="field" part="field field-email">
          <label for="bb-contact-email" part="label label-email">
            Email <span class="hint" part="hint">(optional)</span>
          </label>
          <input
            id="bb-contact-email"
            part="input input-email"
            type="email"
            autocomplete="email"
            placeholder="you@example.com"
            .value=${this._email}
            ?disabled=${off}
            @input=${this._onEmailInput}
          />
        </div>
        <button
          class="cta"
          part="cta"
          type="submit"
          ?disabled=${ctaOff}
        >
          ${this._busy
            ? html`<span
                  class="bb-btn-spinner"
                  part="btn-spinner"
                  aria-hidden="true"
                ></span>Saving…`
            : 'Continue'}
        </button>
      </form>
    `
  }

  private _onPhoneInput = (e: Event) => {
    this._phone = (e.target as HTMLInputElement).value
    // Clear the error eagerly once the field becomes valid; only show it
    // again on blur if it slips back to invalid.
    if (this._phoneOk) this._showPhoneError = false
  }

  private _onEmailInput = (e: Event) => {
    this._email = (e.target as HTMLInputElement).value
  }

  private _onSubmit = (e: Event) => {
    e.preventDefault()
    if (this.disabled || !this.active || this._busy) return
    if (!this._formOk) {
      this._showPhoneError = !this._phoneOk
      return
    }
    this._busy = true
    this._emitAffordance('contact-continue')
    this.dispatchEvent(
      new CustomEvent('set-contact', {
        detail: {
          phone: this._phone.trim(),
          email: this._email.trim() || undefined,
        },
        bubbles: true,
        composed: true,
      }),
    )
  }

  private _emitAffordance(kind: string, context?: Record<string, unknown>) {
    this.dispatchEvent(
      new CustomEvent('widget-affordance', {
        detail: { kind, ...(context ? { context } : {}) },
        bubbles: true,
        composed: true,
      }),
    )
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'bb-chat-contact': BbChatContact
  }
}
